import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faUser } from '@fortawesome/free-solid-svg-icons';
import CustomFieldRenderer from 'components/forms/custom_fields/CustomFieldRenderer';
import { Ticket } from './TicketTypes';
import '../../style/scss/card.scss';

interface TicketRequesterCardProps {
    ticket: Ticket;
    loadStatus: 'idle' | 'loading' | 'loaded';
}

// A read only field of the requester card with its label and one or more values
const RequesterField: React.FC<{ label: string; values: React.ReactNode[] }> = ({ label, values }) => {
    const { t } = useTranslation();

    // Hide the field when it has no values
    const filledValues = values.filter(value => value !== null && value !== undefined && value !== '');
    if (filledValues.length === 0) return null;

    return (
        <div className='ticket-requester-field'>
            <label>{t(label)}</label>
            {filledValues.map((value, index) => <p key={index}>{value}</p>)}
        </div>
    );
};

// The read only requester card of a ticket with the organization and the person of the requester
const TicketRequesterCard: React.FC<TicketRequesterCardProps> = ({ ticket, loadStatus }) => {
    const { t } = useTranslation();

    // Determine the organization and the person, an organization requester has no person
    const reporter = ticket.reporter_contact ?? null;
    const organization = reporter?.entity_type === 'organization' ? reporter : ticket.reporter_organization ?? null;
    const person = reporter && reporter.entity_type !== 'organization' ? reporter : null;

    // Render the linked contact line with its icon and the link to the contact page
    const renderContactLink = (contact: Record<string, any>, type: 'organization' | 'person') => (
        <div className='linked-contact'>
            <FontAwesomeIcon className='linked-contact-icon' icon={type === 'organization' ? faBuilding : faUser} />
            <Link className='linked-contact-link' to={`/contacts/${type}/${contact.id}`}>
                {contact.name}
            </Link>
        </div>
    );

    // Render the phone numbers of a contact as plain values without their types
    const getPhoneNumbers = (contact: Record<string, any>) =>
        (contact.phone_numbers ?? []).map((phoneNumber: Record<string, any>) => phoneNumber.phone_number);

    // Render the email addresses of a contact as plain values without their types
    const getEmailAddresses = (contact: Record<string, any>) =>
        (contact.email_addresses ?? []).map((emailAddress: Record<string, any>) => emailAddress.email);

    // Render the custom fields of the organization as read only values
    const renderCustomFields = (contact: Record<string, any>) =>
        (contact.custom_fields ?? []).map((field: Record<string, any>) => (
            <div className='ticket-requester-field' key={field.field_id}>
                <label>{field.label}</label>
                <p>
                    <CustomFieldRenderer field={field} value={field.value} editable={false} objectType='contact' itemId={contact.id} />
                </p>
            </div>
        ));

    // Render the loader while the ticket loads
    const renderLoader = () => loadStatus === 'loading' ? <div className='loader' /> : null;

    return (
        <>
            {(organization || loadStatus === 'loading') && (
                <div className='card contact-card ticket-requester-card'>
                    <div className='header'>
                        <h6>{t('ticket.general.organization_card_title')}</h6>
                    </div>
                    {renderLoader()}
                    {loadStatus === 'loaded' && organization && (
                        <div className='ticket-requester-block'>
                            {renderContactLink(organization, 'organization')}
                            <RequesterField label='ticket.general.requester_type_label'
                                            values={[organization.contact_type ? t(`organization.contact_type.${organization.contact_type}`) : null]} />
                            <RequesterField label='ticket.general.requester_labels_label'
                                            values={[(organization.labels ?? []).map((label: Record<string, any>) => label.name).join(', ')]} />
                            <RequesterField label='ticket.general.requester_phone_label' values={getPhoneNumbers(organization)} />
                            {renderCustomFields(organization)}
                        </div>
                    )}
                </div>
            )}
            <div className='card contact-card ticket-requester-card'>
                <div className='header'>
                    <h6>{t('ticket.general.reporter_label')}</h6>
                </div>
                {renderLoader()}
                {loadStatus === 'loaded' && (
                    person ? (
                        <div className='ticket-requester-block'>
                            {renderContactLink(person, 'person')}
                            <RequesterField label='ticket.general.requester_phone_label' values={getPhoneNumbers(person)} />
                            <RequesterField label='ticket.general.requester_email_label' values={getEmailAddresses(person)} />
                            <RequesterField label='ticket.general.requester_role_label' values={[person.role]} />
                        </div>
                    ) : (
                        <p className='ticket-requester-empty'>
                            {organization ? t('ticket.general.organization_is_requester_message') : t('ticket.general.no_requester_message')}
                        </p>
                    )
                )}
            </div>
        </>
    );
};

export default TicketRequesterCard;
